import React, { useEffect, useState } from 'react';
import { FileText, ShieldCheck, PenLine, CheckSquare } from 'lucide-react';
import { api } from '../lib/api';
import { isPlatform } from '../lib/capabilities';
import './Onboarding.css';

export default function Onboarding({ user, notify, navigate, onCompleted }) {
  const [agreements, setAgreements] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ signed_name: user?.name || '', title: user?.title || '', legal_entity_name: '', npi: '' });
  const [checks, setChecks] = useState({ authority: false, tos: false, baa: false });

  useEffect(() => {
    let live = true;
    api('/onboarding').then(d => { if (live) setAgreements(d); }).catch(e => notify(e.message));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggle = k => () => setChecks(c => ({ ...c, [k]: !c[k] }));
  const ready = form.signed_name.trim() && form.title.trim() && form.legal_entity_name.trim() && checks.authority && checks.tos && checks.baa;

  async function submit() {
    setBusy(true);
    try {
      await api('/onboarding/sign', {
        method: 'POST',
        body: {
          authority_checked: checks.authority,
          tos_agreed: checks.tos,
          baa_agreed: checks.baa,
          signed_name: form.signed_name.trim(),
          title: form.title.trim(),
          legal_entity_name: form.legal_entity_name.trim(),
          npi: form.npi.trim(),
        },
      });
      await onCompleted();
      notify('Agreements accepted. Welcome to Physicians Health Portal.');
      navigate(isPlatform(user) ? '/admin/dashboard' : '/facility');
    } catch (e) { notify(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="onboarding-page">
      <section className="content-card">
        <div className="content-heading">
          <div className="eyebrow">ACCOUNT ONBOARDING</div>
          <h3>Terms of Service &amp; Business Associate Agreement</h3>
          <p>Before your portal account is activated, an authorized representative of the practice or facility must read and accept both documents below.</p>
        </div>
        {!agreements ? <div className="content-card generation-skeleton" role="status"><i/><i/><i/><span>Loading agreements…</span></div> : (
          <form className="admin-form" onSubmit={e => { e.preventDefault(); submit(); }}>
            <div className="agreement-panels">
              {(agreements?.agreements || []).map(a => (
                <div key={a.type} className="agreement-panel">
                  <div className="agreement-panel-head">
                    <ShieldCheck size={18}/><strong>{a.title}</strong><span className="badge neutral">Version {a.version}</span>
                  </div>
                  <div className="agreement-body">{a.content}</div>
                </div>
              ))}
            </div>

            <div className="agreement-checks">
              <label className="check-row"><input type="checkbox" checked={checks.authority} onChange={toggle('authority')} /><span>I have authority to bind the Covered Entity (the practice or facility).</span></label>
              <label className="check-row"><input type="checkbox" checked={checks.tos} onChange={toggle('tos')} /><span>I have read and agree to the <strong>Terms of Service</strong>.</span></label>
              <label className="check-row"><input type="checkbox" checked={checks.baa} onChange={toggle('baa')} /><span>I have read and agree to the <strong>Business Associate Agreement</strong>.</span></label>
            </div>

            <div className="agreement-fields">
              <label>Legal name (signatory)<input value={form.signed_name} onChange={set('signed_name')} maxLength={200} autoComplete="name" placeholder="Full legal name"/></label>
              <label>Title<input value={form.title} onChange={set('title')} maxLength={120} placeholder="e.g. Physician, Administrator"/></label>
              <label>Practice / facility legal name<input value={form.legal_entity_name} onChange={set('legal_entity_name')} maxLength={200} placeholder="The Covered Entity's legal name"/></label>
              <label>NPI (if applicable)<input value={form.npi} onChange={set('npi')} maxLength={10} inputMode="numeric" placeholder="10-digit NPI or leave blank"/></label>
            </div>

            <p className="modal-note">By selecting <strong>Sign &amp; continue</strong>, you electronically sign these documents. A dated, versioned, immutable record — including a PDF snapshot of the exact text you accepted — is retained with your acceptance metadata.</p>
            <div className="agreement-actions">
              <button className="button primary" disabled={!ready || busy} type="submit">{busy ? 'Recording acceptance…' : <><PenLine size={16}/>Sign &amp; continue</>}</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
